import clsx from "clsx";
import React, { createContext, isValidElement, useContext } from "react";

export const CellIndexContext = createContext("");

const flattenChildren = (children: React.ReactNode) => {
  const flatList: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === React.Fragment) {
      flatList.push(...flattenChildren(child.props.children));
    } else {
      flatList.push(child);
    }
  });

  return flatList;
};

const TdCell = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const context = useContext(CellIndexContext);
  return <td className={clsx(context, className)}>{children}</td>;
};

const ThCell = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const context = useContext(CellIndexContext);
  return <th className={clsx(context, className)}>{children}</th>;
};

const Table = ({
  children,
  classNames = [],
  className,
}: {
  children: React.ReactNode;
  classNames?: string[];
  className?: string;
}) => {
  return (
    <table
      className={clsx(
        className,
        "w-full table-fixed text-16 mt-[3.625rem]",
        "[&>tbody]:font-medium [&>tbody]:text-secondary [&>tbody]:text-center",
        "[&>thead>tr]:flex [&>thead>tr]:w-full [&>thead>tr]:border-b [&>thead>tr]:border-secondary",
        "[&>tbody>tr]:flex [&>tbody>tr]:w-full [&>tbody>tr]:border-b [&>tbody>tr]:border-gray",
        "[&>thead>tr>th]:h-[3.5rem] [&>thead>tr>th]:px-[0.625rem] [&>thead>tr>th]:grid [&>thead>tr>th]:items-center",
        "[&>tbody>tr>td]:h-[3.5rem] [&>tbody>tr>td]:px-[0.625rem] [&>tbody>tr>td]:grid [&>tbody>tr>td]:items-center",
        ...classNames
          .map((c, index) =>
            c
              .split(" ")
              .map((d) => `[&>thead>tr>th:nth-child(${index + 1})]:${d}`),
          )
          .flat(),
        ...classNames
          .map((c, index) =>
            c
              .split(" ")
              .map((d) => `[&>tbody>tr>td:nth-child(${index + 1})]:${d}`),
          )
          .flat(),
      )}
    >
      {React.Children.map(children, (section: any) => {
        return React.cloneElement(section, {
          children: React.Children.map(section.props.children, (row) => {
            return React.cloneElement(row, {
              children: flattenChildren(row.props.children).map(
                (cell: any, cellIndex) => {
                  if (cell?.type === "th") {
                    return (
                      <CellIndexContext.Provider
                        key={cellIndex}
                        value={classNames[cellIndex]}
                      >
                        <ThCell {...cell.props} />
                      </CellIndexContext.Provider>
                    );
                  }

                  if (cell?.type === "td") {
                    return (
                      <CellIndexContext.Provider
                        key={cellIndex}
                        value={classNames[cellIndex]}
                      >
                        <TdCell {...cell.props} />
                      </CellIndexContext.Provider>
                    );
                  }

                  return (
                    <CellIndexContext.Provider
                      key={cellIndex}
                      value={classNames[cellIndex]}
                    >
                      {cell}
                    </CellIndexContext.Provider>
                  );
                },
              ),
            });
          }),
        });
      })}
    </table>
  );
};

export default Table;
